import React, { useRef, useState, createRef, useEffect } from "react";
import {
  LineTextBlack,
  NoteText,
  TitleTextBlack,
} from "../mass_import_styles.jsx";
import AddAccount from "../../../locations/foundation/manage_accounts/add_account.jsx";
import styled from "styled-components";
import Button from "../../../components/Button";
import { Box } from "@mui/system";
import {
  createHierarchyFromMassImport,
  findAccountType,
} from "../../../locations/foundation/manage_accounts/utils.jsx";
import { setDoc, doc } from "firebase/firestore";
import LoadingLogo from "../../../assets/logos/animated-logo.svg?react";
import * as Sentry from "@sentry/react";

// Every child that already resolved committed its account via addDoc before
// we stopped, so the org is left with accounts written and no hierarchy
// entry for them. The operator cannot know to clean that up unless we say so.
const PARTIAL_WRITE_NOTICE =
  "Some accounts may already have been created, and the account hierarchy " +
  "was not updated. Check the chart of accounts before retrying.";

const reportMassAddFailure = (org, reason, detail) => {
  Sentry.withScope((scope) => {
    scope.setTag("component", "MassAddAccounts");
    scope.setTag("operation", "mass_add_accounts.submit_all");
    scope.setTag("reason", reason);
    scope.setExtra("org_id", org);
    scope.setExtra("detail", detail?.message ?? detail);
    if (detail instanceof Error) Sentry.captureException(detail);
    else Sentry.captureMessage(`Mass account import halted: ${reason}`);
  });
};

const LoadingLogoStyled = styled(LoadingLogo)`
  z-index: 5;
  width: 50%;
`;

const MassAddAccounts = ({ org, db, transactionsArg, setShowModal }) => {
  const [groupsAdded, setGroupsAdded] = useState({});
  const [groupsMapping, setGroupsMapping] = useState({});
  const [submitting, setSubmitting] = useState(false);
  // A refusal handleSubmit reported to us (see handleSubmitAll) — shown
  // instead of leaving the modal silently stuck.
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    console.log("Groups Added to Parent: ", groupsAdded);
  }, [groupsAdded]);

  const addGroupToList = (groupObj, accountNumber) => {
    if (groupObj.group !== "") {
      setGroupsAdded((prevGroups) => {
        const existingTypeObject = prevGroups[groupObj.type] || {};
        const existingGroupArray = existingTypeObject[groupObj.group] || [];

        // Check if the array already contains the account number
        if (existingGroupArray.includes(accountNumber)) {
          return prevGroups; // Return the previous state without changes
        }

        // Otherwise, update the state with the new value
        return {
          ...prevGroups,
          [groupObj.type]: {
            ...existingTypeObject,
            [groupObj.group]: [...existingGroupArray, accountNumber],
          },
        };
      });

      // Also update the mapping for this specific account
      setGroupsMapping((prevMapping) => ({
        ...prevMapping,
        [accountNumber]: { type: groupObj.type, group: groupObj.group },
      }));
    }
  };

  // Create a set to keep track of which accounts have already been added
  const uniqueAccounts = new Set();
  const accountDataToAdd = transactionsArg
    .reduce((accumulator, transaction) => {
      const linesArray = transaction.lines.map((line) => {
        return {
          accountNumber: line.account,
          possibleAccountName: line.accountText,
        };
      });

      // Filter out duplicates based on accountNumber
      const uniqueLinesArray = linesArray.filter((line) => {
        if (!uniqueAccounts.has(line.accountNumber)) {
          uniqueAccounts.add(line.accountNumber);
          return true;
        }
        return false;
      });

      return [...accumulator, ...uniqueLinesArray];
    }, [])
    .filter(
      (accountData) => findAccountType(accountData.accountNumber) !== "Equity",
    );

  console.log("accountDataToAdd: ", accountDataToAdd);

  const childRefs = useRef(
    accountDataToAdd.reduce((acc, accountData) => {
      acc[accountData.accountNumber] = createRef();
      return acc;
    }, {}),
  );

  const splitDataAndSort = accountDataToAdd.reduce(
    (acc, accountData) => {
      const groupData = groupsMapping[accountData.accountNumber];
      if (!groupData) {
        acc.ungrouped.push(accountData);
      } else {
        const type = groupData.type;
        const group = groupData.group;
        acc.grouped[type] = acc.grouped[type] || {};
        (acc.grouped[type][group] = acc.grouped[type][group] || []).push(
          accountData,
        );
      }
      return acc;
    },
    { ungrouped: [], grouped: {} },
  );

  const { ungrouped, grouped } = splitDataAndSort;

  const validateAllChildrenForms = async () => {
    // Use Promise.all to wait for all the child components' validation promises to resolve
    const validationResults = await Promise.all(
      Object.values(childRefs.current).map((ref) => ref.current.validateForm()),
    );
    console.log("Validation Results: ", validationResults);

    // Return true if all the child components' validation functions returned true
    return validationResults.every((result) => result);
  };

  const handleSubmitAll = async () => {
    setSubmitError(null);
    setSubmitting(true);
    const isValid = await validateAllChildrenForms();
    if (!isValid) {
      setSubmitting(false);
      return;
    } else {
      // If all the forms are valid, submit them
      // A rejection here used to be swallowed into `undefined`, which then fell
      // through to createHierarchyFromMassImport and threw again on .find().
      // Routed to the error channel now that one exists.
      let accountIds;
      try {
        accountIds = await Promise.all(
          Object.values(childRefs.current).map((ref) =>
            ref.current.handleSubmit(),
          ),
        );
      } catch (err) {
        reportMassAddFailure(org, "submit_rejected", err);
        setSubmitError(PARTIAL_WRITE_NOTICE);
        setSubmitting(false);
        return;
      }

      // handleSubmit returns { error } on a refusal it detected itself — the
      // reserved-Equity backstop in useSubmitAccount is the one that fires here
      // (GH #430/#446). Such an entry has no { [accountNumber]: id } key, so
      // feeding it to createHierarchyFromMassImport puts `undefined` in the
      // hierarchy and the setDoc below rejects unhandled. Stop instead, and say
      // what state the org is in.
      //
      // Near-unreachable in normal use: accountDataToAdd already drops Equity
      // rows by number above. If it does fire, the data is off-model, which is
      // why it goes to Sentry and not just the screen.
      const refusal = accountIds.find((entry) => entry?.error);
      if (refusal) {
        reportMassAddFailure(org, "row_refused", refusal.error);
        setSubmitError(`${refusal.error} ${PARTIAL_WRITE_NOTICE}`);
        setSubmitting(false);
        return;
      }

      // Build the hierarchy using the account IDs
      const accountsHierarchy = createHierarchyFromMassImport(
        groupsMapping,
        accountIds,
      );
      await setDoc(
        doc(db, "orgs", org, "organizationAndSettings", "accountsHierarchy"),
        accountsHierarchy,
      );
      setShowModal(false);
      console.log("Submitting...");
    }
  };

  return (
    <div>
      <React.Fragment>
        {submitting && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
            }}>
            <LoadingLogoStyled />
            <TitleTextBlack>Adding Accounts...</TitleTextBlack>
          </div>
        )}
      </React.Fragment>
      <React.Fragment>
        {!submitting && (
          <React.Fragment>
            <TitleTextBlack>We found these new accounts...</TitleTextBlack>{" "}
            <LineTextBlack>
              Please verify the details, add groups, and submit.
            </LineTextBlack>
          </React.Fragment>
        )}
        {submitError && (
          <NoteText
            role="alert"
            data-testid="mass-add-accounts-error"
            aria-label="Mass account import error"
            style={{ color: "#c62828" }}>
            {submitError}
          </NoteText>
        )}
        {Object.keys(groupsMapping).length > 0 && (
          <NoteText>
            Note: Accounts are moved to the sorted list at the bottom when a
            group is entered
          </NoteText>
        )}
        {accountDataToAdd.length > 0 && (
          <Box
            sx={
              submitting
                ? { display: "none" }
                : {
                    display: "flex",
                    flexDirection: "column",
                    maxHeight: "65vh",
                    overflow: "scroll",
                    pt: 2,
                  }
            }>
            {ungrouped.map((accountData) => (
              <AddAccount
                ref={childRefs.current[accountData.accountNumber]}
                key={`AddAccountComponent-${accountData.accountNumber}`}
                accountNumberToAdd={accountData.accountNumber}
                possibleAccountNameToAdd={accountData.possibleAccountName}
                accounts={[]}
                org={org}
                db={db}
                accountsHierarchy={{}}
                massAdd
                addGroup={addGroupToList}
                groupsAddedToParent={groupsAdded}
              />
            ))}
            {Object.keys(grouped).map((typeKey) => (
              <React.Fragment key={`Type-${typeKey}`}>
                <h2>{typeKey}</h2>
                {Object.keys(grouped[typeKey]).map((groupKey) => (
                  <React.Fragment key={`Group-${groupKey}`}>
                    <h3>Group: {groupKey}</h3>
                    {grouped[typeKey][groupKey].map((accountData) => (
                      <AddAccount
                        ref={childRefs.current[accountData.accountNumber]}
                        key={`AddAccountComponent-${accountData.accountNumber}`}
                        accountNumberToAdd={accountData.accountNumber}
                        possibleAccountNameToAdd={
                          accountData.possibleAccountName
                        }
                        accounts={[]}
                        org={org}
                        db={db}
                        accountsHierarchy={{}}
                        massAdd
                        addGroup={(groupObj) => {
                          addGroupToList(groupObj, accountData.accountNumber);
                        }}
                        groupsAddedToParent={groupsAdded}
                        thisGroup={groupKey}
                      />
                    ))}
                  </React.Fragment>
                ))}
              </React.Fragment>
            ))}
          </Box>
        )}
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
            marginTop: "1rem",
          }}>
          {!submitting && (
            <Button
              disabled={submitting}
              variant="contained"
              onClick={handleSubmitAll}>
              Submit All
            </Button>
          )}
        </div>
      </React.Fragment>
    </div>
  );
};

export default MassAddAccounts;
